import { TablePaginationConfig } from "antd";
import { tripModelsApi } from "api/trips";
import { driverStatisticsApi } from "api/driver";
import { useDataLoadingContext } from "contexts/DataLoadingContext";
import { useNotificationContext } from "contexts/NotificationContext";
import { ITableData, defaultTableData } from "models";
import { ITripsModel } from "models/trips";
import { ChangeEvent, useEffect, useState } from "react";
import moment from "moment";

export default function useTrip() {
  const { showError } = useNotificationContext();
  const { reloadTrip } = useDataLoadingContext();
  const [data, setData] = useState<ITableData<ITripsModel>>(defaultTableData);
  const [todayCancellations, setTodayCancellations] = useState<Map<string, number>>(new Map());

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>(moment().format("YYYY-MM-DD"));

  // LOCAL SEARCH DEBOUNCE
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setData((prev) => ({ ...prev, search: searchValue, page: 1 }));
    }, 500);

    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    fetchData();
    fetchDriverCancellations();
    // eslint-disable-next-line
  }, [data.page, data.search, statusFilter, dateFilter, reloadTrip]);

  const fetchData = async () => {
    setData((prev) => ({ ...prev, loading: true }));
    // Fetch all trips - the API should return cancelled, pending, declined, etc.
    const { data: response, error } = await tripModelsApi(
      data.page,
      data.per_page,
      data.search,
      statusFilter === "all" ? undefined : statusFilter,
      dateFilter && dateFilter !== "all" ? dateFilter : undefined,
      true // Enable Caching
    );
    if (response) {
      // Show all trips including: cancelled, pending, declined (if exists), ongoing, completed
      response.data = response.data.map((value) => ({
        ...value,
        key: value.id,
      }));
      setData((prev) => ({ ...prev, ...response }));
    } else showError(error!.message);
    setData((prev) => ({ ...prev, loading: false }));
  };

  // Fetch driver cancellation counts from driver statistics API
  const fetchDriverCancellations = async () => {
    try {
      const today = moment().format("YYYY-MM-DD");
      const { data: statisticsResponse } = await driverStatisticsApi(today, 100);

      if (statisticsResponse?.data) {
        const cancellationMap = new Map<string, number>();

        // Get cancellation counts from most_declined_requests
        // Note: If backend provides a specific cancellation metric, use that instead
        if (statisticsResponse.data.most_declined_requests) {
          statisticsResponse.data.most_declined_requests.forEach((item) => {
            if (item.driver?.id) {
              cancellationMap.set(item.driver.id, item.metric_value);
            }
          });
        }

        setTodayCancellations(cancellationMap);
      }
    } catch (error) {
      // Silently fail - cancellation count is not critical
      console.warn("Failed to fetch driver cancellation statistics:", error);
    }
  };

  const handlePageChange = ({ current }: TablePaginationConfig) => {
    if (current) setData((prev) => ({ ...prev, page: current }));
  };

  const handleSearch = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(value ?? "");
  };

  const handleStatusChange = (status: string) => {
    setStatusFilter(status);
    setData((prev) => ({ ...prev, page: 1 })); // reset page to 1 on tab change
  };

  const handleDateChange = (date: string) => {
    setDateFilter(date);
    setData((prev) => ({ ...prev, page: 1 }));
  };

  return { 
    data: { ...data, search: searchValue }, // Return the local search value for the input
    statusFilter, 
    dateFilter, 
    todayCancellations, 
    handlePageChange, 
    handleSearch, 
    handleStatusChange, 
    handleDateChange 
  };
}
